/**
 * Edit Result Dialog Composable
 *
 * Handles editing individual test results.
 *
 * @param key - Unique key for state isolation
 */
export const useEditResultDialog = (key: string = "default") => {
  //
  // Composables
  //

  // Localization
  const { t } = useI18n();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  // Pending API
  const pendingComposable = usePending(`validation.pending`);

  // Auth - get logged-in user
  const { userName } = useAuth();

  //
  // State
  //

  const isOpen = useState<boolean>(`editResult.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`editResult.${key}.loading`, () => false);
  const selectedResult = useState<any | null>(`editResult.${key}.selectedResult`, () => null);

  //
  // Computed Properties
  //

  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    result: selectedResult.value,
    loading: loading.value,
    onSave: save,
    onCancel: close,
  }));

  //
  // Methods
  //

  /**
   * Open dialog with result data
   */
  const open = (result: any): void => {
    selectedResult.value = {
      id: result.id,
      testName: result.lisName || result.name,
      testCode: result.testCode,
      result: result.displayResult || result.result,
      resultType: result.resultType,
      allowedValues: result.allowedValues,
      units: result.units,
      rangeText: result.rangeText,
      rangeMin: result.rangeMin,
      rangeMax: result.rangeMax,
    };
    isOpen.value = true;
  };

  /**
   * Close dialog and reset state
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — old data stays visible during
    // the close animation. State will be replaced by the next open() call.
  };

  /**
   * Save edited result
   */
  const save = async (editedData: { id: number; newResult: string; reason?: string }): Promise<void> => {
    if (!selectedResult.value) return;

    loading.value = true;

    try {
      const success = await pendingComposable.updateResult(
        editedData.id,
        editedData.newResult,
        userName.value,
        editedData.reason,
        selectedResult.value.result, // Pass original value for first edit tracking
      );

      if (!success) {
        throw new Error("Failed to update result");
      }

      showSnackbar(t("pages.validation.dialogs.editResult.success"), {
        icon: "mdi-file-edit-outline",
        color: "warning",
      });

      close();
    } catch (error: any) {
      showSnackbar(error.message || t("pages.validation.dialogs.editResult.error"), {
        icon: "mdi-alert-circle",
        color: "error",
      });
    } finally {
      loading.value = false;
    }
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading: readonly(loading),
    selectedResult: readonly(selectedResult),

    // Computed
    componentProps,

    // Methods
    open,
    close,
    save,
  };
};
